import { useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import {
  Column,
  GenericEntityType,
  GenericProviderType,
  KindEnum,
  Pagination,
  Provider,
  TemplateEntity,
  getLabelAndValue,
  selectTemplateEntity,
} from '@agilelab/plugin-wb-platform';
import { ApolloClient, gql } from '@apollo/client/core';
import { contextProviderFactory } from '../../utils';
import { useColumns } from '../../hooks';

import { PolicyFilters, PolicyFiltersSection } from './PolicyFilters';
import { useApolloClient } from '@apollo/client';
import {
  PolicyQuery,
  Severity,
  Status,
} from '@agilelab/plugin-wb-governance-common';

export type PolicyTypeEntity = GenericEntityType & {};

interface PolicyContextType
  extends GenericProviderType<PolicyTypeEntity[], PolicyFilters> {}

const [usePolicyContext, PolicyContextProvider] =
  contextProviderFactory<PolicyContextType>();

export default usePolicyContext;

export interface PolicyProviderProps {
  templateKind: TemplateEntity | undefined;
  children: React.ReactNode;
  parentTemplateRef?: string;
}

const defaultColumns = new Map<string, string>([
  ['name', '{{name}} {{version}}'],
  ['id', '{{governance_entity_id}}'],
  ['status', '{{status}}'],
  ['severity', '{{content.severity}}'],
]);

export const GET_POLICIES_LIST = gql`
  query GET_POLICIES_LIST(
    $limit: Int
    $offset: Int
    $where: cgp_governance_entity_bool_exp
    $order_by: [cgp_governance_entity_order_by!]
  ) {
    cgp_governance_entity(
      limit: $limit
      offset: $offset
      where: $where
      order_by: $order_by
    ) {
      governance_entity_id
      version
      name
      description
      resource_type {
        uuid
        name
        displayName: display_name
      }
      status
      trigger
      engine
      strategy
      timing
      content
    }
    cgp_governance_entity_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`;

export const PolicyProvider: React.FC<PolicyProviderProps> = props => {
  const { templateKind, children } = props;

  const [filters, setFilters] = useState<PolicyFilters>({});

  const client = useApolloClient();
  const [count, setCount] = useState<number>(0);
  const [pagination, setPagination] = useState<Pagination>({
    limit: 10,
    currentPage: 0,
  });

  const columns: Column[] = useColumns(defaultColumns, templateKind);

  const buildSeverity = (severity: Severity[]) => {
    const rows = severity.map(s => ({
      content: { _contains: { severity: s } },
    }));
    const query = {
      _or: rows,
    };
    return query;
  };

  const policyState = useAsync(async () => {
    const { data } = await client.query<PolicyQuery>({
      query: GET_POLICIES_LIST,
      variables: {
        where: {
          name: { _ilike: `%${filters.search || ''}%` },
          description: { _ilike: `%${filters.text || ''}%` },
          trigger: { _eq: 'passive' },
          status: {
            ...(filters.status?.length && { _in: filters.status }),
            _neq: Status.Deleted,
          },
          ...(filters.severity &&
            filters.severity.length > 0 &&
            buildSeverity(filters.severity)),
          resource_type: {
            name: {
              _eq: templateKind?.filter?.resource_type,
            },
          },
          governance_entity_environments: {
            environment: {
              name: { _eq: templateKind?.filter?.environment || '' },
            },
          },
        },
        limit: pagination.limit,
        offset: pagination.currentPage,
      },
      fetchPolicy: 'no-cache',
    });

    setCount(data.cgp_governance_entity_aggregate.aggregate.count);
    const adaptedPolicy = data?.cgp_governance_entity?.map(policy => ({
      __metadata: {
        name: `${policy.name} ${policy.version}`,
        kind: KindEnum.policy,
      },
      ...policy,
    }));

    return adaptedPolicy;
  }, [props.templateKind, filters, pagination.limit, pagination.cursor]);

  return (
    <PolicyContextProvider
      value={{
        entitiesState: policyState,
        columns,
        filters,
        changeFilters: <K extends keyof PolicyFilters>(
          key: K,
          filterValue: PolicyFilters[K],
        ) => {
          setPagination(p => ({ ...p, currentPage: 0 }));
          setFilters(f => ({ ...f, [key]: filterValue }));
        },
        resetFilters: () => {
          setFilters({});
        },
        templateKind,
        pagination,
        setPagination,
        count,
      }}
    >
      {children}
    </PolicyContextProvider>
  );
};

export class PolicyHandlerProvider
  implements Provider<PolicyTypeEntity[], PolicyFilters>
{
  private readonly apolloClient: ApolloClient<any>;
  private readonly templateEntity: TemplateEntity | undefined;

  constructor(
    apolloClient: ApolloClient<any>,
    availableKinds: TemplateEntity[],
  ) {
    this.apolloClient = apolloClient;
    this.templateEntity = selectTemplateEntity(availableKinds, KindEnum.policy);
  }

  useContext = usePolicyContext;

  renderContextProvider(
    templateKind: TemplateEntity | undefined,
    children: React.ReactNode,
    parentTemplateRef?: string,
  ) {
    return (
      <PolicyProvider
        templateKind={templateKind}
        parentTemplateRef={parentTemplateRef}
      >
        {children}
      </PolicyProvider>
    );
  }

  filtersSection = PolicyFiltersSection;

  adapt(entity: GenericEntityType) {
    return {
      name: entity.__metadata.name,
      kind: entity.__metadata.kind,
      label: `${entity.name} ${entity.version}`,
    };
  }

  async resolve(id: string) {
    try {
      const { data } = await this.apolloClient.query<PolicyQuery>({
        query: GET_POLICIES_LIST,
        variables: {
          where: {
            governance_entity_id: { _eq: id },
          },
        },
        fetchPolicy: 'no-cache',
      });

      const result = data?.cgp_governance_entity?.[0];

      if (result) {
        const entity: GenericEntityType = {
          __metadata: {
            name: `${result.name} ${result.version}`,
            kind: KindEnum.policy,
          },
          ...result,
        };
        return getLabelAndValue(
          entity,
          this.adapt(entity),
          this.templateEntity,
        );
      }

      return undefined;
    } catch (error) {
      throw new Error(`Error while resolving policy with ref: ${id}`);
    }
  }
}
